import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import Pill from '../components/common/Pill';
import { eventsPills, waterEventsPills } from '../constants/pillConstants';
import {
  DETAIL_EVENT_TYPES,
  EXPANDABLE_EVENT_TYPES,
  NO_TOTAL_TIME_EVENTS,
} from '../constants/tablesConfig';
import eventNameMapping from '../constants/events';

const getEventName = (eventType) => eventNameMapping[eventType] || eventType;

/**
 * Gets the pill variant (color) based on the event type.
 *
 * @param {string} eventType - The type of event.
 * @returns {string} The corresponding color variant for the pill.
 */
const getPillVariant = (eventType) => eventsPills[eventType] || eventsPills.default;

/*const formatTime = (timestamp, use12HourFormat = true) =>
  new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: use12HourFormat,
    timeZone: 'UTC',
  });
*/
const formatDate = (timestamp) =>
  new Date(timestamp).toLocaleDateString('en-US', { timeZone: 'UTC' });

/*const formatTimeRange = (end, duration) => {
  const start = new Date(new Date(end).getTime() - duration);
  return `${formatTime(start)} – ${formatTime(end)}`;
};*/

export const formatTimeRangeFromTimeStamp = (timestamp, duration) => {
  const date = new Date(timestamp);

  const [minutes, seconds] = duration.split(':').map(Number);
  const totalDurationMs = (minutes * 60 + seconds) * 1000; // Convert to milliseconds

  const endDate = new Date(date.getTime() + totalDurationMs);

  const formatTo12Hour = (dateObj) => {
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Convert 0 to 12 for AM
    return `${hours}:${minutes} ${ampm}`;
  };

  const startTime = formatTo12Hour(date);
  const endTime = formatTo12Hour(endDate);

  return `${startTime} - ${endTime}`;
};

/**
 * Normalizes raw event data into a structured format for the Usage History View
 *
 * This function processes raw API event data to create a chronological list of events
 * with session grouping. Key features include:
 * - Session separation with StartRow markers
 * - Duration and time range calculations
 * - Event type mapping and Pill component integration for visual styling
 *
 * @param {Array} events - Raw event data from API endpoint
 * @returns {Array} - Normalized table rows ready for display, containing:
 *   - session start markers (type: 'start')
 *   - event rows (type: 'normal') with formatted data and Pill components
 */
export const normalizeEventsForUsageHistory = (events) => {
  const sessions = normalizeEventsBase(events[0]?.items);
  const sortedSessions = Object.values(sessions).sort(
    (a, b) => new Date(b.sessionData.timestamp) - new Date(a.sessionData.timestamp),
  );

  return sortedSessions.flatMap((session) => {
    const sessionStartRow = session.sessionData.startRow;

    let currentLocation = null;
    const normalizedRows = [];

    for (const event of session.events) {
      const eventEndTime = new Date(event.timestamp);
      eventEndTime.setMinutes(eventEndTime.getMinutes() + (event.duration || 0));
      const isLocationChange = event.event_type === 'location_change_event';

      const eventName = getEventName(event.event_type);
      const pillVariant = getPillVariant(event.event_type);

      const location =
        event.event_type === 'transit_event'
          ? 'N/A'
          : event.location || event.current_location || currentLocation;
      let rowType = EXPANDABLE_EVENT_TYPES.has(event.event_type)
        ? 'expandableEvent'
        : DETAIL_EVENT_TYPES.has(event.event_type)
        ? 'indentedDetail'
        : 'normal';

      const duration = isLocationChange ? 'N/A' : formatDurationInSeconds(event.duration);

      const transformedSubevents = event?.subevents?.map((subevent, index, array) => {
        const subeventName = getEventName(subevent.event_type);
        const subeventPillVariant = getPillVariant(subevent.event_type);
        const duration = formatDurationInSeconds(subevent.duration);
        const isLast = index === array.length - 1;
        const isDetailSubRow = DETAIL_EVENT_TYPES.has(subevent.event_type);
        const showNaForDuration = NO_TOTAL_TIME_EVENTS.has(subevent.event_type);
        const durationValue = showNaForDuration ? 'N/A' : duration;

        return {
          isLastRow: isLast,
          isDetailSubRow,
          eventId: subevent.id,
          columns: [
            { id: 'location', value: '' },
            {
              id: 'eventName',
              value: <Pill text={subeventName} variant={subeventPillVariant} />,
              isIndentationColumn: true,
            },
            {
              id: 'timeRange',
              value: subevent?.time_range,
            },
            { id: 'duration', value: durationValue },
            { id: 'date', value: formatDate(subevent.timestamp) },
          ],
        };
      });

      normalizedRows.push({
        id: event.id,
        type: rowType,
        subEvents: transformedSubevents,
        data: {
          columns: [
            { id: 'idEvent', value: event.id },
            { id: 'location', value: location },
            { id: 'eventName', value: <Pill text={eventName} variant={pillVariant} /> },
            {
              id: 'timeRange',
              value: event?.time_range,
            },
            { id: 'duration', value: duration },
            { id: 'date', value: formatDate(event.timestamp) },
          ],
          sessionId: session.sessionData.id,
        },
      });

      if (event.current_location) currentLocation = event.current_location;
    }

    return [sessionStartRow, ...normalizedRows];
  });
};

const formatToLongDate = (dateInput) => {
  const date = new Date(dateInput);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/**
 * Creates a "Session Start" row for a given session
 * @param {number} sessionId - Unique session identifier
 * @param {string} timestamp - Timestamp of the session start
 * @returns {Object} - Row data for session start
 */
const createSessionStartRow = (sessionId, timestamp) => {
  return {
    id: `session-start-${sessionId}`,
    type: 'start',
    data: {
      text: `Session Start - ${formatToLongDate(timestamp)}`,
      sessionId: sessionId,
    },
  };
};

/**
 * Creates a "Unit Day Start" row for a given unit day
 * @param {number} sessionId - Unique session identifier
 * @param {string} timestamp - Timestamp of the session start
 * @returns {Object} - Row data for session start
 */
const createUnitDayStartRow = (unitDayId, timestamp) => ({
  id: `session-start-${unitDayId}`,
  type: 'start',
  data: {
    text: `Starting Date: ${new Date(timestamp).toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })}`,
    sessionId: unitDayId,
  },
});

function groupEventsByLocation(events) {
  let groups = [];
  let currentGroup = [];

  events.forEach((event) => {
    if (event.event_type === 'location_change_event') {
      if (currentGroup.length > 0) {
        groups.push({ location: currentGroup[0].location, events: currentGroup });
      }
      currentGroup = [event];
    } else if (event.event_type === 'transit_event' && currentGroup.length > 0) {
      currentGroup.push(event);
      groups.push({ location: currentGroup[0].current_location, events: currentGroup });
      currentGroup = [];
    } else if (currentGroup.length > 0) {
      currentGroup.push(event);
    }
  });

  return groups.filter(
    (group) =>
      group.events.some((event) => event.event_type === 'location_change_event') &&
      group.events.some((event) => event.event_type === 'transit_event'),
  );
}

/**
 * Creates a "Cycle Start" row for a given water change session.
 * @param {number|string} id - Unique session identifier.
 * @param {string} timestamp - Timestamp of the cycle start.
 * @returns {Object} - Row data for cycle start.
 */
const createCycleStartRow = (id, timestamp, sessionId) => ({
  id: `cycle-start-${id}`,
  type: 'start',
  data: {
    text: `Cycle Start #${id}`,
    id,
  },
});

/**
 * Creates a "Cycle End" row for a given water change session.
 * @param {number|string} sessionId - Unique session identifier.
 * @param {number} totalDurationSeconds - Total duration of the cycle in seconds.
 * @returns {Object} - Row data for cycle end.
 */
const createCycleEndRow = (id, totalDurationSeconds) => ({
  id: `cycle-end-${id}`,
  type: 'end',
  data: {
    text: `Cycle #${id} Total Duration`,
    totalDuration: (
      <Pill size="small" variant="gray" className="rounded-xl" text={totalDurationSeconds} />
    ),
    id,
  },
});

export const formatDuration = (durationInMiliSeconds) => {
  const totalSeconds = Math.floor(durationInMiliSeconds / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  let durationValue;

  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
    durationValue = 'N/A';
  } else {
    if (hours > 0) {
      durationValue = `${hours}h ${minutes < 10 ? '0' : ''}${minutes}m`;
    } else {
      durationValue = `${minutes}m ${seconds < 10 ? '0' : ''}${seconds}s`;
    }
  }

  return durationValue;
};

export const formatDurationInSeconds = (durationInSeconds) => {
  const totalSeconds = Math.floor(durationInSeconds);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

  let durationValue;

  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
    durationValue = 'N/A';
  } else {
    if (hours > 0) {
      durationValue = `${hours}h ${formattedMinutes}m ${formattedSeconds}s`;
    } else {
      durationValue = `${formattedMinutes}m ${formattedSeconds}s`;
    }
  }
  return durationValue;
};

export const formatDurationInSecondsWithMiliSeconds = (durationInSeconds) => {
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = Math.floor(durationInSeconds % 60);
  const milliseconds = Math.floor((durationInSeconds % 1) * 100);
  let durationValue;

  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
  const formattedMilliseconds = milliseconds < 10 ? `0${milliseconds}` : milliseconds;

  if (isNaN(minutes) || isNaN(seconds)) {
    durationValue = 'N/A';
  } else {
    durationValue = `${formattedMinutes}m ${formattedSeconds}s ${formattedMilliseconds}ms`;
  }

  return durationValue;
};

const processLocationEvents = (events) => {
  const filteredEvents = events.filter((e) =>
    ['mopping_event', 'no_activity_event'].includes(e.event_type),
  );

  if (filteredEvents.length === 0) return null;

  filteredEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const moppingDuration = filteredEvents
    .filter((e) => e.event_type === 'mopping_event')
    .reduce((sum, e) => sum + (e.mop_duration || 0), 0);
  const otherTasksDuration = filteredEvents
    .filter((e) => e.event_type === 'no_activity_event')
    .reduce((sum, e) => sum + (e.duration_without_mopping_time || 0), 0);

  const totalDuration = moppingDuration + otherTasksDuration;

  const startTime = new Date(filteredEvents[0].timestamp);
  const endTime = new Date(startTime.getTime() + totalDuration * 1000); // Convert to milliseconds

  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  };

  return {
    totalDuration: formatDuration(totalDuration), // Use formatted duration
    moppingDuration: formatDuration(moppingDuration), // Use formatted duration
    otherTasksDuration: formatDuration(otherTasksDuration), // Use formatted duration
    timeRange: `${startTime.toLocaleTimeString(
      'en-US',
      timeOptions,
    )} - ${endTime.toLocaleTimeString('en-US', timeOptions)}`,
    date: startTime.toLocaleDateString('en-US', { timeZone: 'UTC' }),
  };
};

/**
 * Converts a duration given as "hh:mm:ss" or "mm:ss" into a
 * human-readable string:
 *   • When hours > 0  → "HHhrs. MMmins. SSsecs."
 *   • When hours = 0  → "MMmins. SSsecs."
 *
 * Examples:
 *   formatDuration("01:05:07") // "01hrs. 05mins. 07secs."
 *   formatDuration("00:03:02") // "03mins. 02secs."
 *   formatDuration("27:31:06") // "27hrs. 31mins. 06secs."
 *   formatDuration("03:02")    // "03mins. 02secs."
 *
 * @param  durationStr Duration as "hh:mm:ss" or "mm:ss"
 * @return             Formatted string per the rules above
 */
export const formatDurationToHsMnsSgs = (durationStr) => {
  if (typeof durationStr !== 'string') return '00m 00s';

  const parts = durationStr.split(':');
  if (parts.length !== 2 && parts.length !== 3) return '00m. 00s';

  const nums = parts.map((p) => parseInt(p, 10));
  if (nums.some((n) => Number.isNaN(n) || n < 0)) return '00m. 00s';

  const [hours = 0, minutes, seconds] = parts.length === 3 ? nums : [0, ...nums];

  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  if (hours > 0) {
    const hh = String(hours).padStart(2, '0');
    return `${hh}hs ${mm}m ${ss}s`;
  }
  return `${mm}m ${ss}s.`;
};

/**
 * Converts a duration string in the format "MM:SS:CS" (minutes:seconds:centiseconds)
 * to a string "XXm YYs", where XX and YY are always two digits with leading zeros.
 *
 * Example:
 *   formatToMinSec("01:05:57")  // → "01m 05s"
 *   formatToMinSec("27:31:06")  // → "27m 31s"
 *   formatToMinSec("00:03:02")  // → "00m 03s"
 *
 * @param {string} durationStr – Duration in "MM:SS:CS"
 * @returns {string} – Duration as "XXm YYs"
 */
export const formatToMinSec = (durationStr) => {
  if (typeof durationStr !== 'string') {
    return '00m 00s';
  }

  const parts = durationStr.split(':');
  if (parts.length !== 3) {
    return '00m 00s';
  }

  const [minStr, secStr] = parts;
  const minutes = parseInt(minStr, 10);
  const seconds = parseInt(secStr, 10);

  if (isNaN(minutes) || isNaN(seconds)) {
    return '00m 00s';
  }

  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  return `${mm}m ${ss}s`;
};

/**
 * Normalizes events into a generic structure without grouping by location.
 * This function serves as a base for different event normalization needs.
 * @param {Array} events - Raw events from API
 * @returns {Object} - Object mapping session IDs to their respective events
 */
const normalizeEventsBase = (events) => {
  if (!Array.isArray(events)) return {};

  return events.reduce((acc, event) => {
    const sessionId = event.session_id;

    if (event?.is_start_of_session) {
      acc[sessionId] = {
        sessionData: {
          id: `session-${sessionId}`,
          timestamp: event.timestamp,
          startRow: createSessionStartRow(sessionId, event.timestamp),
        },
        events: [],
      };
    }

    if (!acc[sessionId]) {
      acc[sessionId] = acc[sessionId] = {
        sessionData: {
          id: `session-${sessionId}`,
          timestamp: event.timestamp,
        },
        events: [],
      };
    }

    acc[sessionId].events.push(event);
    return acc;
  }, {});
};

/**
 * Normalizes events into groups depending on the unit day.
 * @param {Array} events - Raw events from API
 * @returns {Object} - Object mapping Unit Day IDs to their respective events
 */
export const normalizeEventsForLocationHistoryV2 = (events) => {
  let normalizedRows = [];

  events.forEach((event) => {
    const eventId = uuidv4();
    if (event.is_start_of_session) normalizedRows.push(createSessionStartRow(eventId, event.date));

    normalizedRows.push({
      id: eventId,
      type: 'normal',
      data: {
        columns: [
          { id: 'location', value: event.locations },
          {
            id: 'total_duration',
            value: formatDurationToHsMnsSgs(event.total_duration),
          },
          {
            id: 'time_range',
            value: event.time_range,
          },
          {
            id: 'mopping_duration',
            value: formatDurationToHsMnsSgs(event.mopping_duration),
          },
          {
            id: 'other_tasks_duration',
            value: formatDurationToHsMnsSgs(event?.no_activity_duration),
          },
          { id: 'date', value: event.date },
        ],
      },
    });
  });

  return normalizedRows;
};

/**
 * Normalizes events specifically for the Location History Table
 * @param {Array} events - Raw events from API
 * @returns {Array} - Normalized rows for the table
 */
export const normalizeEventsForLocationHistory = (events) => {
  const sessions = normalizeEventsBase(events);

  const sortedSessions = Object.values(sessions).sort(
    (a, b) => new Date(b.sessionData.timestamp) - new Date(a.sessionData.timestamp),
  );

  return sortedSessions.flatMap((session) => {
    const locationGroups = groupEventsByLocation(session.events);

    const validRows = locationGroups
      .map((group, index) => {
        const processed = processLocationEvents(group.events);
        if (!processed) return null;

        return {
          id: `${session.sessionData.id}-${group.location}-${index}`,
          type: 'normal',
          data: {
            sessionId: session.sessionData.id,
            columns: [
              { id: 'location', value: group.location || 'Unknown' },
              { id: 'totalDuration', value: `${processed.totalDuration}` },
              { id: 'moppingDuration', value: `${processed.moppingDuration}` },
              { id: 'otherTasksDuration', value: `${processed.otherTasksDuration}` },
              { id: 'timeRange', value: processed.timeRange },
              { id: 'date', value: processed.date },
            ],
          },
        };
      })
      .filter(Boolean);

    if (validRows.length === 0) return [];

    return [session.sessionData.startRow, ...validRows];
  });
};

/**
 * Normalizes raw water change event data into a structured format for the Water Change View.
 * Each session (grouped by session_id) will include a Cycle Start row, followed by individual event rows,
 * and ending with a Cycle End row displaying the total cycle duration.
 *
 * @param {Array} events - Raw water change events from API.
 * @returns {Array} - Normalized table rows ready for display.
 */
export const normalizeEventsForWaterChanges = (events) => {
  if (!Array.isArray(events)) return [];

  const cyclesaMap = [];
  let subArray = [];

  events.forEach((event) => {
    if (event.is_start_of_cycle && subArray.length) {
      cyclesaMap.push(subArray);
      subArray = [];
    }

    subArray.push(event);

    if (event.cycle_total_duration) {
      cyclesaMap.push(subArray);
      subArray = [];
    }
  });

  if (subArray.length) cyclesaMap.push(subArray);

  const sessions = Array.from(cyclesaMap.entries()).map(([cycleId, sessionEvents]) => ({
    cycleId,
    events: sessionEvents,
    startTimestamp: sessionEvents[0].timestamp,
    day: sessionEvents[0].date,
    unitDayId: sessionEvents[0].unit_day_id,
  }));

  const sessionsByDay = sessions.reduce((acc, session) => {
    const day = session.unitDayId;
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(session);
    return acc;
  }, {});

  let sortedKeys = Object.keys(sessionsByDay).sort((a, b) => b - a);

  const dailyRows = [];

  sortedKeys.forEach((day) => {
    let dayRows = [];
    let cycleEndRow = null;

    sessionsByDay[day].forEach((session) => {
      const { sessionId, events } = session;
      const sessionRows = [];

      events.forEach((event) => {
        if (event.is_start_of_unit_day) {
          sessionRows.push(createUnitDayStartRow(event.unit_day_id, event.timestamp));
        }

        if (event?.unit_day_total_duration || event?.unit_day_total_duration === 0) {
          sessionRows.push({
            id: `total-per-day-${event?.unit_day_id}`,
            type: 'end',
            className: 'text-md font-bold',
            data: {
              text: `Total Duration for  ${event?.date}`,
              totalDuration: (
                <Pill
                  size="small"
                  variant="green"
                  className="text-[#237E2C] rounded-xl"
                  text={formatDurationInSeconds(event.unit_day_total_duration)}
                />
              ),
            },
          });
        }

        if (event?.cycle_total_duration) {
          cycleEndRow = createCycleEndRow(
            event.cycle_id,
            formatDurationInSeconds(event?.cycle_total_duration),
          );
          sessionRows.push(cycleEndRow);
        }

        sessionRows.push({
          id: `${event?.event_id}-${event?.action}`,
          type: 'indented',
          data: {
            columns: [
              {
                id: 'event',
                value: (
                  <Pill
                    text={event.action}
                    variant={
                      waterEventsPills[event.action.toLowerCase()] || waterEventsPills.default
                    }
                  />
                ),
                isIndentationColumn: event.action !== 'Transit',
              },
              {
                id: 'duration',
                value: event.duration_seconds
                  ? formatDurationInSeconds(event.duration_seconds)
                  : 'N/A',
              },
              { id: 'timeRange', value: event.time_range },
              { id: 'date', value: event.date },
            ],
            sessionId,
          },
        });

        if (event.is_start_of_cycle) {
          cycleEndRow = null;
          sessionRows.push(
            createCycleStartRow(event.cycle_id, event.timestamp, event?.unit_day_id),
          );
        }
      });

      dayRows = dayRows.concat(sessionRows);
    });

    dailyRows.push(...dayRows);
  });

  return dailyRows;
};

export const isBatteryZero = (percentage) => Number(percentage) === 0;

export const formatBatteryPercentage = (value, showPercentage = false) => {
  if (isBatteryZero(value)) {
    return 'N/A';
  }

  return showPercentage ? `${value}%` : value;
};

export const normalizeEventsForBatteryLife = (events) => {
  if (!Array.isArray(events)) return [];

  const normalizedRows = [];

  events.forEach((event) => {
    const eventId = uuidv4();

    normalizedRows.push({
      id: eventId,
      type: 'normal',
      data: {
        columns: [
          { id: 'percentage', value: formatBatteryPercentage(event?.percentage, true) },
          {
            id: 'time',
            value: event?.time,
          },
          {
            id: 'date',
            value: event?.date,
          },
        ],
      },
    });
  });

  return normalizedRows;
};
